import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Download } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "jai-admin-key";

const fmtDate = (iso) => new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
const fmtMoney = (v) => (v ? `$${(v / 1_000_000).toFixed(1)}M` : "—");

const STATUS_META = {
  new: { label: "New", dot: "#00E676" },
  review: { label: "Under Review", dot: "#FFC107" },
  contacted: { label: "Contacted", dot: "#00B0FF" },
  closed: { label: "Closed", dot: "#9CA3AF" },
};

const StatusCell = ({ kind, id, value, onChange }) => {
  const current = STATUS_META[value] ? value : "new";
  const meta = STATUS_META[current];
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] pl-2.5 pr-1.5 py-1"
      data-testid={`status-${kind}-${id.slice(0, 8)}`}
    >
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ background: meta.dot, boxShadow: `0 0 8px ${meta.dot}` }}
      />
      <select
        value={current}
        onChange={(e) => onChange(kind, id, e.target.value)}
        data-testid={`status-select-${kind}-${id.slice(0, 8)}`}
        className="bg-transparent text-xs text-white/80 outline-none cursor-pointer [&>option]:bg-[#0A0E17]"
      >
        {Object.entries(STATUS_META).map(([k, m]) => (
          <option key={k} value={k}>{m.label}</option>
        ))}
      </select>
    </div>
  );
};

export default function Admin() {
  const [key, setKey] = useState(() => localStorage.getItem(KEY) || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers: { "X-Admin-Key": k } });
      setData(res.data);
      localStorage.setItem(KEY, k);
      setKey(k);
    } catch (e) {
      setData(null);
      if (e.response?.status === 401) {
        setError("Wrong passcode. Try again.");
        localStorage.removeItem(KEY);
        setKey("");
      } else {
        setError("Couldn't reach the server.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (kind, id, status) => {
    const collKey = kind === "query" ? "queries" : kind === "site" ? "sites" : "projects";
    try {
      await axios.patch(
        `${API}/admin/leads/${kind}/${id}/status`,
        { status },
        { headers: { "X-Admin-Key": key } }
      );
      setData((d) => ({
        ...d,
        [collKey]: d[collKey].map((item) => (item.id === id ? { ...item, status } : item)),
      }));
      toast.success(`Marked as ${STATUS_META[status].label}`);
    } catch {
      toast.error("Couldn't update status — try again.");
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Type", "Ref", "Client Name", "Phone", "Email", "Location", "Category", "Budget", "Purpose", "Building Height", "Status", "Received"],
    ];
    data.queries.forEach((q) =>
      rows.push(["General Query", q.ref || "", q.name, q.phone || "", q.email, "", q.interest || q.category, q.budget ? `$${q.budget.toLocaleString()}` : "", "", "", q.status || "new", q.created_at])
    );
    data.sites.forEach((s) =>
      rows.push(["Site Submission", s.ref || "", s.owner_name, s.phone || "", s.email, s.suburb, "Land / JV", "", s.intent, "", s.status || "new", s.created_at])
    );
    (data.projects || []).forEach((p) =>
      rows.push(["Project Query", p.ref || "", p.name, p.phone || "", p.email, p.location || "", p.category, p.budget_range || "", p.purpose === "occupy" ? "Occupy / Own" : "Investment / Lease", p.height === "single" ? "Single Storey" : "Double / Multi-Storey", p.status || "new", p.created_at])
    );
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replaceAll('"', '""')}"`).join(",")).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "jai-homes-leads.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length - 1} leads to CSV`);
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0E17] flex items-center justify-center px-6" data-testid="admin-login">
        <div className="glass rounded-2xl border border-white/10 p-10 w-full max-w-sm text-center">
          <div className="flex justify-center mb-6"><Logo width={90} /></div>
          <Lock size={20} className="mx-auto text-[#FF1744] mb-3" />
          <h1 className="font-display text-lg font-bold text-white mb-1">Team Access</h1>
          <p className="text-xs text-white/50 mb-6">Enter the admin passcode to view enquiries.</p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              load(input);
            }}
          >
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Passcode"
              data-testid="admin-passcode-input"
              className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-[#FF1744] focus:ring-2 focus:ring-[#FF1744]/25 transition-colors duration-300"
            />
            {error && <p className="mt-3 text-xs text-[#FF1744]" data-testid="admin-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="mt-5 w-full rounded-full py-3 font-display text-sm font-bold text-white bg-gradient-to-r from-[#FF3D00] to-[#FF1744] hover:shadow-[0_0_24px_rgba(255,23,68,0.5)] transition-all duration-300 disabled:opacity-50"
            >
              {loading ? "Checking..." : "Unlock"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E17] px-6 sm:px-12 py-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-5">
            <Logo width={80} />
            <div>
              <h1 className="font-display text-2xl font-extrabold text-white tracking-tight">Enquiry Inbox</h1>
              <p className="text-xs text-white/50">
                {data.queries.length} query-wizard leads · {data.sites.length} site submissions · {(data.projects || []).length} project queries
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportCsv}
              data-testid="admin-export-csv-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/40 px-4 py-2 font-display text-xs font-semibold text-[#00E676] hover:bg-[#00E676]/10 transition-colors duration-300"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              onClick={() => load(key)}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-display text-xs font-semibold text-white/70 hover:text-white hover:border-white/40 transition-colors duration-300"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={() => { localStorage.removeItem(KEY); setKey(""); setData(null); }}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#FF1744]/40 px-4 py-2 font-display text-xs font-semibold text-[#FF1744] hover:bg-[#FF1744]/10 transition-colors duration-300"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>

        <section className="mb-12">
          <h2 className="font-display text-sm font-bold tracking-[0.25em] text-[#00E676] mb-4" data-testid="admin-queries-heading">QUERY WIZARD LEADS</h2>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-left text-xs" data-testid="admin-queries-table">
              <thead className="bg-white/[0.04] text-white/50 font-display tracking-wide">
                <tr>
                  {["Received", "Name", "Category", "Interest", "Budget", "Email", "Phone", "Message", "Status"].map((h) => (
                    <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-white/80">
                {data.queries.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-white/40">No enquiries yet.</td></tr>
                )}
                {data.queries.map((q) => (
                  <tr key={q.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                    <td className="px-4 py-3 whitespace-nowrap text-white/50">{fmtDate(q.created_at)}</td>
                    <td className="px-4 py-3 font-semibold text-white">{q.name}</td>
                    <td className="px-4 py-3 capitalize">{q.category}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{q.interest}</td>
                    <td className="px-4 py-3">{fmtMoney(q.budget)}</td>
                    <td className="px-4 py-3">{q.email}</td>
                    <td className="px-4 py-3">{q.phone || "—"}</td>
                    <td className="px-4 py-3 max-w-[220px] truncate">{q.message || "—"}</td>
                    <td className="px-4 py-3">
                      <StatusCell kind="query" id={q.id} value={q.status} onChange={updateStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="font-display text-sm font-bold tracking-[0.25em] text-[#00B0FF] mb-4" data-testid="admin-sites-heading">SITE SUBMISSIONS</h2>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-left text-xs" data-testid="admin-sites-table">
              <thead className="bg-white/[0.04] text-white/50 font-display tracking-wide">
                <tr>
                  {["Received", "Owner", "Suburb / LGA", "Lot Size", "Zoning", "Intent", "Email", "Phone", "Details", "Status"].map((h) => (
                    <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-white/80">
                {data.sites.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-white/40">No site submissions yet.</td></tr>
                )}
                {data.sites.map((s) => (
                  <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                    <td className="px-4 py-3 whitespace-nowrap text-white/50">{fmtDate(s.created_at)}</td>
                    <td className="px-4 py-3 font-semibold text-white">{s.owner_name}</td>
                    <td className="px-4 py-3">{s.suburb}</td>
                    <td className="px-4 py-3">{s.lot_size || "—"}</td>
                    <td className="px-4 py-3">{s.zoning || "—"}</td>
                    <td className="px-4 py-3 capitalize">{s.intent}</td>
                    <td className="px-4 py-3">{s.email}</td>
                    <td className="px-4 py-3">{s.phone || "—"}</td>
                    <td className="px-4 py-3 max-w-[220px] truncate">{s.details || "—"}</td>
                    <td className="px-4 py-3">
                      <StatusCell kind="site" id={s.id} value={s.status} onChange={updateStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-display text-sm font-bold tracking-[0.25em] text-[#FF1744] mb-4" data-testid="admin-projects-heading">PROJECT QUERY WIZARD LEADS</h2>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-left text-xs" data-testid="admin-projects-table">
              <thead className="bg-white/[0.04] text-white/50 font-display tracking-wide">
                <tr>
                  {["Received", "Name", "Category", "Purpose", "Height", "Location", "Land", "Config", "Budget", "Email", "Phone", "Notes", "Status"].map((h) => (
                    <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-white/80">
                {(data.projects || []).length === 0 && (
                  <tr><td colSpan={13} className="px-4 py-8 text-center text-white/40">No project queries yet.</td></tr>
                )}
                {(data.projects || []).map((p) => (
                  <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                    <td className="px-4 py-3 whitespace-nowrap text-white/50">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-3 font-semibold text-white whitespace-nowrap">{p.name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{p.category}</td>
                    <td className="px-4 py-3">{p.purpose === "occupy" ? "Occupy / Own" : "Investment / Lease"}</td>
                    <td className="px-4 py-3">{p.height === "single" ? "Single" : "Multi-Storey"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{p.location || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{p.land_size || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{p.configuration || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{p.budget_range || "—"}</td>
                    <td className="px-4 py-3">{p.email}</td>
                    <td className="px-4 py-3">{p.phone}</td>
                    <td className="px-4 py-3 max-w-[220px] truncate">{p.notes || "—"}</td>
                    <td className="px-4 py-3">
                      <StatusCell kind="project" id={p.id} value={p.status} onChange={updateStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-display text-sm font-bold tracking-[0.25em] text-[#FFC107] mb-4" data-testid="admin-reviews-heading">CLIENT REVIEWS — MODERATION</h2>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-left text-xs" data-testid="admin-reviews-table">
              <thead className="bg-white/[0.04] text-white/50 font-display tracking-wide">
                <tr>
                  {["Received", "Name", "Category", "Suburb", "Rating", "Review", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-white/80">
                {(data.reviews || []).length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-white/40">No reviews submitted yet.</td></tr>
                )}
                {(data.reviews || []).map((r) => (
                  <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                    <td className="px-4 py-3 whitespace-nowrap text-white/50">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 font-semibold text-white whitespace-nowrap">{r.name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{r.category}</td>
                    <td className="px-4 py-3">{r.suburb || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#FFC107] font-semibold">{r.rating} / 5</td>
                    <td className="px-4 py-3 max-w-[260px] truncate">{r.comments}</td>
                    <td className="px-4 py-3">
                      <span
                        className="rounded-full px-2.5 py-1 font-display text-[10px] font-bold tracking-wide border"
                        style={{
                          color: r.status === "approved" ? "#00E676" : r.status === "closed" ? "#9CA3AF" : "#FFC107",
                          borderColor: r.status === "approved" ? "#00E67644" : r.status === "closed" ? "#9CA3AF44" : "#FFC10744",
                        }}
                        data-testid={`review-status-${r.id.slice(0, 8)}`}
                      >
                        {(r.status || "pending").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.status !== "approved" && (
                        <button
                          onClick={() => updateStatus("review", r.id, "approved")}
                          data-testid={`review-approve-${r.id.slice(0, 8)}`}
                          className="rounded-full border border-[#00E676]/40 px-3 py-1 font-display text-[11px] font-semibold text-[#00E676] hover:bg-[#00E676]/10 transition-colors duration-300 mr-2"
                        >
                          Approve
                        </button>
                      )}
                      {r.status !== "closed" && (
                        <button
                          onClick={() => updateStatus("review", r.id, "closed")}
                          data-testid={`review-archive-${r.id.slice(0, 8)}`}
                          className="rounded-full border border-white/15 px-3 py-1 font-display text-[11px] font-semibold text-white/50 hover:bg-white/[0.06] transition-colors duration-300"
                        >
                          Archive
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
